import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Crown, Sparkles, Globe, RefreshCw, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuth } from '@/client/hooks/use-auth';
import { useTimezone } from '@/client/hooks/use-timezone';
import { updateUser, changePassword } from '@/client/lib/auth-client';
import { SettingsLayout } from '@/client/components/layout/settings-layout';
import { Button } from '@/client/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/client/components/ui/card';
import { Badge } from '@/client/components/ui/badge';
import { Input } from '@/client/components/ui/input';
import { Label } from '@/client/components/ui/label';
import { getTimezoneOptionsByRegion, getTimezoneOffset } from '@/client/stores/timezone-store';
import {
  updateProfileSchema,
  changePasswordSchema,
  type UpdateProfileInput,
  type ChangePasswordInput,
} from '@/shared/schemas/auth';

export function SettingsPage() {
  const { user, isPlusUser, refreshSession } = useAuth();
  const {
    effectiveTimezone,
    browserTimezone,
    utcOffset,
    updateTimezone,
    detectTimezone,
    isUpdating,
    isDetecting,
  } = useTimezone();

  // Profile form state
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const profileForm = useForm<UpdateProfileInput>({
    resolver: zodResolver(updateProfileSchema),
    defaultValues: {
      name: user?.name || '',
    },
  });

  // Password form state
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const passwordForm = useForm<ChangePasswordInput>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  });

  // Handle profile update
  const handleProfileUpdate = async (data: UpdateProfileInput) => {
    setIsUpdatingProfile(true);
    try {
      const result = await updateUser({ name: data.name });
      if (result.error) {
        toast.error(result.error.message || 'Failed to update profile');
        return;
      }
      toast.success('Profile updated successfully');
      refreshSession();
    } catch {
      toast.error('Failed to update profile');
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  // Handle password change
  const handlePasswordChange = async (data: ChangePasswordInput) => {
    setIsChangingPassword(true);
    try {
      const result = await changePassword({
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
        revokeOtherSessions: true,
      });
      if (result.error) {
        const message = result.error.message || 'Failed to change password';
        // Better error messages for common cases
        if (
          message.toLowerCase().includes('invalid') ||
          message.toLowerCase().includes('incorrect')
        ) {
          toast.error('Current password is incorrect');
        } else {
          toast.error(message);
        }
        return;
      }
      toast.success('Password changed successfully');
      passwordForm.reset();
    } catch {
      toast.error('Failed to change password');
    } finally {
      setIsChangingPassword(false);
    }
  };

  // Handle timezone change
  const handleTimezoneChange = (newTimezone: string) => {
    updateTimezone(newTimezone);
    toast.success('Timezone updated successfully');
  };

  // Handle auto-detect
  const handleDetectTimezone = () => {
    detectTimezone();
    toast.info('Detecting timezone from your location...');
  };

  // Group timezones by region
  const timezonesByRegion = getTimezoneOptionsByRegion();

  return (
    <SettingsLayout>
      {/* Subscription Status */}
      <Card className="rounded-2xl py-0">
        <CardHeader className="pb-0">
          <CardTitle>Subscription</CardTitle>
          <CardDescription>Manage your plan and billing</CardDescription>
        </CardHeader>
        <CardContent className="py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div
                className={`size-12 rounded-xl flex items-center justify-center ${
                  isPlusUser ? 'bg-gradient-to-br from-primary/20 to-accent/20' : 'bg-muted'
                }`}
              >
                <Crown
                  className={`size-6 ${isPlusUser ? 'text-primary' : 'text-muted-foreground'}`}
                />
              </div>
              <div>
                <p className="font-semibold flex items-center gap-2">
                  {isPlusUser ? 'Plus' : 'Free'} Plan
                  {isPlusUser && (
                    <Badge variant="primary-subtle" className="gap-1">
                      <Sparkles className="size-3" />
                      PLUS
                    </Badge>
                  )}
                </p>
                <p className="text-sm text-muted-foreground">
                  {isPlusUser
                    ? 'You have access to all premium features'
                    : 'Upgrade to unlock folders, sharing, and more'}
                </p>
              </div>
            </div>
            {!isPlusUser ? (
              <Button asChild className="rounded-xl">
                <Link to="/settings/billing">
                  <Crown className="size-4" />
                  Upgrade to Plus
                </Link>
              </Button>
            ) : (
              <Button variant="outline" asChild className="rounded-xl">
                <Link to="/settings/billing">Manage billing</Link>
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Profile Section */}
      <Card className="rounded-2xl py-0">
        <CardHeader className="pb-0">
          <CardTitle>Profile Information</CardTitle>
          <CardDescription>Update your account details</CardDescription>
        </CardHeader>
        <CardContent className="py-6 space-y-6">
          <form onSubmit={profileForm.handleSubmit(handleProfileUpdate)}>
            <div className="grid gap-4 max-w-md">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  type="text"
                  {...profileForm.register('name')}
                  className="rounded-xl"
                />
                {profileForm.formState.errors.name && (
                  <p className="text-xs text-destructive">
                    {profileForm.formState.errors.name.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  defaultValue={user?.email}
                  disabled
                  className="rounded-xl"
                />
                <p className="text-xs text-muted-foreground">
                  Contact support to change your email.
                </p>
              </div>
            </div>

            <Button
              type="submit"
              className="rounded-xl mt-6"
              disabled={isUpdatingProfile || !profileForm.formState.isDirty}
            >
              {isUpdatingProfile ? (
                <>
                  <Loader2 className="size-4 animate-spin" />
                  Saving…
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Timezone Section */}
      <Card className="rounded-2xl py-0">
        <CardHeader className="pb-0">
          <div className="flex items-center gap-3">
            <div className="size-10 rounded-xl bg-gradient-to-br from-blue-500/20 to-blue-500/5 flex items-center justify-center">
              <Globe className="size-5 text-blue-600" />
            </div>
            <div>
              <CardTitle>Timezone</CardTitle>
              <CardDescription>
                Used for displaying analytics time data in your local time
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="py-6 space-y-4">
          <div className="grid gap-4 max-w-md">
            <div className="space-y-2">
              <Label htmlFor="timezone">Your Timezone</Label>
              <div className="flex gap-2">
                <select
                  id="timezone"
                  value={effectiveTimezone}
                  onChange={(e) => handleTimezoneChange(e.target.value)}
                  disabled={isUpdating}
                  className="flex-1 h-10 px-3 rounded-xl border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 disabled:opacity-50"
                >
                  {Object.entries(timezonesByRegion).map(([region, options]) => (
                    <optgroup key={region} label={region}>
                      {options.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label} ({getTimezoneOffset(option.value)})
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleDetectTimezone}
                  disabled={isDetecting}
                  className="rounded-xl"
                  title="Auto-detect from your location"
                >
                  {isDetecting ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <RefreshCw className="size-4" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Current offset: <span className="font-medium">{utcOffset}</span>
              </p>
            </div>

            {effectiveTimezone !== browserTimezone && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                <Globe className="size-4 mt-0.5 text-amber-600 flex-shrink-0" />
                <div className="text-xs text-amber-800 dark:text-amber-200">
                  <p className="font-medium">Different from browser timezone</p>
                  <p className="text-amber-700 dark:text-amber-300">
                    Your browser is set to{' '}
                    <button
                      onClick={() => handleTimezoneChange(browserTimezone)}
                      className="underline hover:no-underline"
                    >
                      {browserTimezone}
                    </button>
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="text-xs text-muted-foreground bg-muted/30 p-3 rounded-lg max-w-md">
            <p className="font-medium mb-1">How timezone affects analytics:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Hourly click distribution chart shows times in your timezone</li>
              <li>Time of day analysis reflects your local business hours</li>
              <li>Click data is stored in UTC and converted for display</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Password Section */}
      <Card className="rounded-2xl py-0">
        <CardHeader className="pb-0">
          <CardTitle>Change Password</CardTitle>
          <CardDescription>Update your password to keep your account secure</CardDescription>
        </CardHeader>
        <CardContent className="py-6 space-y-6">
          <form onSubmit={passwordForm.handleSubmit(handlePasswordChange)}>
            <div className="grid gap-4 max-w-md">
              <div className="space-y-2">
                <Label htmlFor="current-password">Current Password</Label>
                <Input
                  id="current-password"
                  type="password"
                  {...passwordForm.register('currentPassword')}
                  className="rounded-xl"
                />
                {passwordForm.formState.errors.currentPassword && (
                  <p className="text-xs text-destructive">
                    {passwordForm.formState.errors.currentPassword.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="new-password">New Password</Label>
                <Input
                  id="new-password"
                  type="password"
                  {...passwordForm.register('newPassword')}
                  className="rounded-xl"
                />
                {passwordForm.formState.errors.newPassword && (
                  <p className="text-xs text-destructive">
                    {passwordForm.formState.errors.newPassword.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm New Password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  {...passwordForm.register('confirmPassword')}
                  className="rounded-xl"
                />
                {passwordForm.formState.errors.confirmPassword && (
                  <p className="text-xs text-destructive">
                    {passwordForm.formState.errors.confirmPassword.message}
                  </p>
                )}
              </div>
            </div>

            <Button type="submit" className="rounded-xl mt-6" disabled={isChangingPassword}>
              {isChangingPassword ? (
                <>
                  <Loader2 className="size-4 animate-spin" />
                  Updating…
                </>
              ) : (
                'Update Password'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </SettingsLayout>
  );
}
